import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFormik } from "formik";
import * as Yup from "yup";
import Popup from "../../../base-components/Popup";
import AuthShell, { AuthField } from "../../../ui/AuthShell";
import {
  memberLoginRequest,
  saveMemberAuth,
  clearMemberAuth,
} from "../../../api/memberPortalClient";
import { getStaffLandingPath } from "../../../utils/roleAccess";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const unifiedLoginSchema = Yup.object().shape({
  identifier: Yup.string()
    .trim()
    .required("Email, phone, CNIC or member code is required."),
  password: Yup.string().required("Password is required."),
});

function isMemberRole(role) {
  const key = String(role || "").toLowerCase();
  return key === "user" || key === "member";
}

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [infoMessage] = useState(location.state?.message || "");

  const showError = (message) => {
    setErrorMessage(message || "Login failed.");
    setIsPopupOpen(true);
    setTimeout(() => setIsPopupOpen(false), 4000);
  };

  const completeMemberLogin = async (identifier, password) => {
    const { ok, body } = await memberLoginRequest(identifier, password);
    if (ok && body.data) {
      saveMemberAuth(body.data);
      sessionStorage.removeItem("tab-index");
      navigate("/member/dashboard", { replace: true });
      return true;
    }
    showError(body?.error?.message || "Invalid credentials.");
    return false;
  };

  const completeStaffLogin = (responseBody) => {
    clearMemberAuth();
    localStorage.setItem("user-info", JSON.stringify(responseBody));
    sessionStorage.setItem("user-info", JSON.stringify(responseBody));
    sessionStorage.removeItem("tab-index");
    const landing = getStaffLandingPath(responseBody?.user);
    navigate(landing, { replace: true });
  };

  const handleLogin = async (values) => {
    const identifier = String(values.identifier || "").trim();
    const password = values.password;
    setSubmitting(true);

    try {
      const looksLikeEmail = identifier.includes("@");

      if (looksLikeEmail) {
        const response = await fetch(`${BASE_URL}users/loginUser`, {
          method: "POST",
          body: JSON.stringify({ email: identifier, password }),
          headers: { "Content-Type": "application/json" },
        });

        if (response.ok) {
          const responseBody = await response.json();
          if (isMemberRole(responseBody?.user?.role)) {
            await completeMemberLogin(identifier, password);
            return;
          }
          completeStaffLogin(responseBody);
          return;
        }
      }

      const memberOk = await completeMemberLogin(identifier, password);
      if (memberOk) return;
    } catch (error) {
      console.error("Login error:", error);
      showError("Unable to reach the server. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const { values, errors, touched, handleBlur, handleChange, handleSubmit } =
    useFormik({
      initialValues: { identifier: "", password: "" },
      validationSchema: unifiedLoginSchema,
      onSubmit: handleLogin,
    });

  const fieldClass = (hasError) =>
    `h-11 w-full rounded-gx-control border bg-gx-surface px-3 text-gx-ink outline-none focus:ring-2 focus:ring-gx-teal ${
      hasError ? "border-gx-red" : "border-gx-border"
    }`;

  return (
    <AuthShell
      title="Sign in"
      subtitle="Staff use email. Members can use email, phone, CNIC or member code."
      footer={
        <p className="text-sm text-gx-ink-soft mt-6">
          Need an account?{" "}
          <Link to="/signup" className="font-semibold text-gx-red">
            Join Now
          </Link>
        </p>
      }
    >
      {infoMessage ? (
        <p className="text-sm text-gx-navy bg-gx-orange/15 border border-gx-orange/40 rounded-gx-control px-3 py-2 mb-4">
          {infoMessage}
        </p>
      ) : null}
      <form onSubmit={handleSubmit} autoComplete="off">
        <AuthField
          id="identifier"
          label="Email, phone, CNIC or member code"
          error={touched.identifier && errors.identifier}
          name="identifier"
          value={values.identifier}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <div className="flex flex-col gap-2 mb-4">
          <label htmlFor="password" className="text-sm font-medium text-gx-ink">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              name="password"
              className={fieldClass(touched.password && errors.password)}
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <button
              type="button"
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 inline-flex items-center justify-center text-gx-ink-soft"
              onClick={() => setShowPassword(!showPassword)}
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
            </button>
          </div>
          {touched.password && errors.password ? (
            <p className="text-sm text-gx-red">{errors.password}</p>
          ) : null}
        </div>
        <div className="flex items-center justify-between mb-5">
          <label className="flex gap-2 items-center text-sm text-gx-ink">
            <input type="checkbox" name="remember" className="h-4 w-4 accent-gx-red" />
            Remember me
          </label>
          <button type="button" className="text-sm font-medium text-gx-red">
            Forgot password?
          </button>
        </div>
        <button type="submit" disabled={submitting} className="gx-cta w-full h-11">
          {submitting ? "Signing in" : "Sign in"}
        </button>
      </form>
      {isPopupOpen && (
        <Popup
          alertType="error"
          message={errorMessage}
          onClose={() => setIsPopupOpen(false)}
        />
      )}
    </AuthShell>
  );
}

export default Login;
